import type {
  AttemptState,
  AttemptConfig,
  AttemptAnswer,
  EngineEvent,
  EventListener,
  EventUnsubscribe,
} from './types';
import { EventEmitter, createEvent } from './events';

/**
 * AttemptManager - Manages content attempt lifecycle
 */
export class AttemptManager {
  private state: AttemptState;
  private config: Required<AttemptConfig>;
  private emitter: EventEmitter = new EventEmitter();

  constructor(config: AttemptConfig) {
    this.config = {
      maxAttempts: 0, // unlimited by default
      allowRetry: true,
      onAttempt: () => {},
      onComplete: () => {},
      ...config,
    };

    this.state = {
      contentId: config.contentId,
      attempts: 0,
      maxAttempts: this.config.maxAttempts,
      isCompleted: false,
      isCorrect: null,
      startTime: Date.now(),
      endTime: null,
      answers: [],
    };
  }

  /**
   * Get current attempt state
   */
  getState(): AttemptState {
    return { ...this.state };
  }

  /**
   * Get content ID
   */
  getContentId(): string {
    return this.state.contentId;
  }

  /**
   * Get current attempt number
   */
  getCurrentAttempt(): number {
    return this.state.attempts;
  }

  /**
   * Get remaining attempts (0 = unlimited)
   */
  getRemainingAttempts(): number {
    if (this.state.maxAttempts === 0) return Infinity;
    return Math.max(0, this.state.maxAttempts - this.state.attempts);
  }

  /**
   * Check if more attempts are allowed
   */
  canAttempt(): boolean {
    if (this.state.isCompleted && this.state.isCorrect) return false;
    if (this.state.maxAttempts === 0) return true;
    return this.state.attempts < this.state.maxAttempts;
  }

  /**
   * Check if retry is allowed
   */
  canRetry(): boolean {
    if (!this.config.allowRetry) return false;
    if (this.state.isCompleted && this.state.isCorrect) return false;
    return this.canAttempt();
  }

  /**
   * Start a new attempt
   */
  start(): void {
    if (!this.canAttempt()) {
      throw new Error('No more attempts allowed');
    }

    this.emit(createEvent('attempt:start', this.state.contentId, {
      attemptNumber: this.state.attempts + 1,
    }));
  }

  /**
   * Submit an answer attempt
   */
  submit(
    answer: unknown,
    isCorrect: boolean,
    score: number = isCorrect ? 1 : 0,
    max: number = 1,
    feedback?: string
  ): AttemptAnswer {
    if (!this.canAttempt()) {
      throw new Error('No more attempts allowed');
    }

    const attemptNumber = this.state.attempts + 1;
    const timestamp = Date.now();
    const timeSpent = timestamp - this.state.startTime;

    const attemptAnswer: AttemptAnswer = {
      attemptNumber,
      answer,
      isCorrect,
      score,
      max,
      timestamp,
      timeSpent,
      feedback,
    };

    this.state.attempts = attemptNumber;
    this.state.answers.push(attemptAnswer);
    this.state.isCorrect = isCorrect;

    if (typeof this.config.onAttempt === 'function') {
      this.config.onAttempt(attemptAnswer);
    }

    this.emit(createEvent('attempt:submit', this.state.contentId, attemptAnswer));

    // Mark as completed if correct or no more attempts
    if (isCorrect || !this.canAttempt()) {
      this.complete();
    }

    return attemptAnswer;
  }

  /**
   * Mark attempt as complete
   */
  private complete(): void {
    this.state.isCompleted = true;
    this.state.endTime = Date.now();

    if (typeof this.config.onComplete === 'function') {
      this.config.onComplete(this.state);
    }

    this.emit(createEvent('attempt:complete', this.state.contentId, this.state));
  }

  /**
   * Reset for retry
   */
  retry(): void {
    if (!this.canRetry()) {
      throw new Error('Retry not allowed');
    }

    // Don't reset attempts count, just allow a new attempt
    this.state.isCompleted = false;
    this.state.isCorrect = null;
    this.state.endTime = null;

    this.emit(createEvent('attempt:retry', this.state.contentId, {
      attemptNumber: this.state.attempts + 1,
    }));
  }

  /**
   * Reset completely (for new content)
   */
  reset(): void {
    this.state = {
      contentId: this.state.contentId,
      attempts: 0,
      maxAttempts: this.config.maxAttempts,
      isCompleted: false,
      isCorrect: null,
      startTime: Date.now(),
      endTime: null,
      answers: [],
    };
  }

  /**
   * Get the latest answer
   */
  getLatestAnswer(): AttemptAnswer | null {
    return this.state.answers[this.state.answers.length - 1] || null;
  }

  /**
   * Get all answers
   */
  getAllAnswers(): AttemptAnswer[] {
    return [...this.state.answers];
  }

  /**
   * Get best answer (highest score)
   */
  getBestAnswer(): AttemptAnswer | null {
    if (this.state.answers.length === 0) return null;
    return this.state.answers.reduce((best, current) =>
      current.score > best.score ? current : best
    );
  }

  /**
   * Get total time spent
   */
  getTimeSpent(): number {
    if (this.state.endTime) {
      return this.state.endTime - this.state.startTime;
    }
    return Date.now() - this.state.startTime;
  }

  /**
   * Subscribe to events
   */
  on<T = unknown>(
    type: 'attempt:start' | 'attempt:submit' | 'attempt:retry' | 'attempt:complete',
    listener: EventListener<T>
  ): EventUnsubscribe {
    return this.emitter.on(type, listener);
  }

  /**
   * Subscribe to all events
   */
  onAny<T = unknown>(listener: EventListener<T>): EventUnsubscribe {
    return this.emitter.onAny(listener);
  }

  /**
   * Emit event
   */
  private emit<T = unknown>(event: EngineEvent<T>): void {
    this.emitter.emit(event);
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.emitter.removeAllListeners();
  }
}

/**
 * Create an attempt manager
 */
export function createAttemptManager(config: AttemptConfig): AttemptManager {
  return new AttemptManager(config);
}
